import { ReactNode, ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppState, Auth0Provider } from '@auth0/auth0-react'
import { useConfigStore } from 'src/state/stores'

const getAuth0CacheLocation = (): 'localstorage' | 'memory' => {
  if (import.meta.env.VITE_ENVIRONMENT === 'pipeline_test') {
    return 'localstorage'
  }

  // Cypress cy.session() persists localStorage, not in-memory Auth0 cache.
  if (typeof window !== 'undefined' && window.Cypress) {
    return 'localstorage'
  }

  if (import.meta.env.DEV) {
    return 'localstorage'
  }

  return 'memory'
}

const AuthenticationProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const navigate = useNavigate()
  const { auth0Domain, auth0ClientID, auth0Scopes, auth0Audience } = useConfigStore(
    ({ auth0Domain, auth0ClientID, auth0Scopes, auth0Audience }) => ({
      auth0Domain,
      auth0ClientID,
      auth0Scopes,
      auth0Audience,
    }),
  )

  const onRedirectCallback = (appState: AppState | undefined) => {
    navigate(appState?.returnTo || window.location.pathname)
  }

  return (
    <Auth0Provider
      domain={auth0Domain || ''}
      clientId={auth0ClientID || ''}
      onRedirectCallback={onRedirectCallback}
      cacheLocation={getAuth0CacheLocation()}
      authorizationParams={{
        redirect_uri: location.origin,
        scope: auth0Scopes,
        audience: auth0Audience,
      }}
    >
      {children}
    </Auth0Provider>
  )
}

export default AuthenticationProvider
